/**
 * KANBAN LIST — elenco delle bacheche, paginato e ordinato server-side.
 *
 * Pattern di riferimento: `PipelineList.tsx` + il join client-side con le pipeline
 * per mostrare il nome della pipeline (come QuotationList fa con le aziende). Il nome
 * apre la board (`/kanban/:id`).
 */

import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { type GridColDef } from '@mui/x-data-grid';
import { getEntityLabelByFqn } from '../../entityRegistry/entityRegistry';
import { useIsClassNameDisabled } from '../../plugin/useCoreItems';
import { useServerSearch, useServerListParams, useListDeleteHandler } from '@crm/shared';
import { ServerDataGrid } from '../../components/ServerDataGrid';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useConfirmDialog } from '../../components/useConfirmDialog';
import { SearchGlyph, PlusGlyph, BoardGlyph, TrashGlyph } from '../../components/icons';
import { fetchPipelines } from '../pipeline/pipelineThunks';
import type { PipelineSliceState } from '../pipeline/pipelineTypes';
import { fetchKanbans, deleteKanban } from './kanbanThunks';
import type { KanbanSliceState } from './kanbanTypes';

export default function KanbanList() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const confirm = useConfirmDialog();
  const { items: allItems, status, error, totalCount: serverTotal } = useAppSelector(
    (state) => state.kanbans as KanbanSliceState,
  );
  // Le board di un tipo spento dalla pagina «Voci del CRM» (FILO-53) non si elencano.
  const isClassNameDisabled = useIsClassNameDisabled();
  const items = useMemo(
    () => allItems.filter((k) => !isClassNameDisabled(k.className)),
    [allItems, isClassNameDisabled],
  );
  // ponytail: il totale del server meno le board nascoste di QUESTA pagina; per un conteggio
  // esatto servirebbe il filtro lato server.
  const totalCount = serverTotal - (allItems.length - items.length);
  const { items: pipelines } = useAppSelector((state) => state.pipelines as PipelineSliceState);
  const pipelineById = useMemo(
    () => new Map(pipelines.map((p) => [p.pipelineId, p.name])),
    [pipelines],
  );

  const { search, setSearch, query } = useServerSearch();
  const {
    paginationModel, setPaginationModel,
    sortModel, setSortModel,
    resetPage,
    backendParams,
  } = useServerListParams();

  useEffect(() => { resetPage(); }, [query, resetPage]);

  useEffect(() => {
    dispatch(fetchKanbans({ ...backendParams, search: query || undefined }));
  }, [backendParams, query, dispatch]);

  useEffect(() => {
    if (pipelines.length === 0) dispatch(fetchPipelines({ pageSize: 1000 }));
  }, [pipelines.length, dispatch]);

  // Delete + refetch coerente con totalCount/paginazione (vedi TS-9).
  const handleDelete = useListDeleteHandler({
    deleteThunk: deleteKanban,
    fetchThunk: fetchKanbans,
    fetchParams: { ...backendParams, search: query || undefined },
    itemsLength: items.length,
    page: paginationModel.page,
    resetPage,
    dispatch,
  });

  const columns = useMemo(
    () => kanbanColumns(confirm, handleDelete, t, pipelineById),
    [confirm, handleDelete, t, pipelineById],
  );

  if (status === 'failed') return <p className="text-error">{t('common.error', { message: error })}</p>;

  return (
    <div>
      {/* Testata come la lista Contatti, che e' la pagina di riferimento del
          nuovo design: titolo e conteggio a sinistra, ricerca e azioni a
          destra, il tutto fuori dalla card. */}
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('kanban.title')}</h1>
          <span className="page-sub">{t('common.record', { count: totalCount })}</span>
        </div>
        <div className="page-actions">
          <div className="searchline">
            <SearchGlyph />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('kanban.searchPlaceholder')}
              aria-label={t('kanban.searchPlaceholder')}
            />
          </div>
          <Link to="/kanban/new" className="btn-primary">
            <PlusGlyph /> {t('kanban.newKanban')}
          </Link>
        </div>
      </div>

      <div className="list-card">
        {status === 'loading' && <p className="list-feedback">{t('common.loading')}</p>}
        {status === 'succeeded' && items.length === 0 && (
          <p className="text-muted list-feedback">{t('kanban.noKanbans')}</p>
        )}
        {items.length > 0 && (
          <ServerDataGrid
            rows={items}
            columns={columns}
            getRowId={(row) => row.kanbanId}
            rowCount={totalCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            loading={status === 'loading'}
            getRowHeight={() => 64}
            className="tk-grid"
          />
        )}
      </div>

      <ConfirmDialog open={confirm.isOpen} message={confirm.message} onConfirm={confirm.confirm} onCancel={confirm.cancel} />
    </div>
  );
}

function kanbanColumns(
  confirm: ReturnType<typeof useConfirmDialog>,
  handleDelete: (kanbanId: number) => void,
  t: (key: string, opts?: Record<string, unknown>) => string,
  pipelineById: Map<number, string>,
): GridColDef[] {
  return [
    {
      field: 'name', headerName: t('kanban.columns.name'), flex: 1,
      renderCell: (params) => (
        <span className="lc-name">
          <Link to={`/kanban/${params.row.kanbanId}`} className="nm">{params.value}</Link>
        </span>
      ),
    },
    {
      // Lookup client-side: il backend non ordina su un campo derivato.
      field: 'pipeline', headerName: t('kanban.columns.pipeline'), width: 200, sortable: false,
      valueGetter: (_value: unknown, row: { pipelineId: number }) =>
        pipelineById.get(row.pipelineId) ?? `#${row.pipelineId}`,
    },
    {
      // TS-117: che cosa contiene la bacheca. Serve a distinguerle a colpo d'occhio adesso che
      // possono essere di entita' diverse, e a capire perche' una non si apre (la guardia).
      field: 'className', headerName: t('kanban.columns.entityType'), width: 160, sortable: false,
      renderCell: (params) => getEntityLabelByFqn(params.value as string, t),
    },
    {
      // Azioni a icona come nella lista contatti: "apri" resta anche come link
      // sul nome, qui e' la scorciatoia in fondo alla riga.
      field: 'actions', headerName: t('kanban.columns.actions'), sortable: false,
      width: 100, align: 'right', headerAlign: 'right',
      renderCell: (params) => (
        <span className="lc-actions">
          <Link to={`/kanban/${params.row.kanbanId}`} className="lc-act" aria-label={t('kanban.openBoard')}>
            <BoardGlyph />
          </Link>
          <button
            type="button"
            className="lc-act del"
            aria-label={t('common.delete')}
            onClick={() => confirm.requestConfirm(
              t('kanban.deleteConfirm', { name: params.row.name }),
              () => handleDelete(params.row.kanbanId),
            )}
          >
            <TrashGlyph />
          </button>
        </span>
      ),
    },
  ];
}
